import { useEffect, useState } from "react";
import { Mail, Send, ChevronDown, ChevronUp } from "lucide-react";
import api from "../lib/api";

export default function AdminOnboarding() {
    const [templates, setTemplates] = useState([]);
    const [queue, setQueue] = useState([]);
    const [open, setOpen] = useState({});
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState("");
    const [previewEmail, setPreviewEmail] = useState("");

    const reload = () => {
        api.get("/admin/onboarding/templates").then((r) => {
            const arr = r.data || [];
            setTemplates(arr.sort((a, b) => a.delay_days - b.delay_days));
            const d = {};
            arr.forEach((t) => { d[t.id] = { ...t }; });
            setDraft(d);
        }).catch(() => {});
        api.get("/admin/onboarding/queue?limit=50").then((r) => setQueue(r.data || [])).catch(() => {});
    };

    useEffect(() => { reload(); }, []);

    const save = async (id) => {
        setSaving(id);
        try {
            const d = draft[id];
            const payload = {
                subject_fr: d.subject_fr,
                subject_en: d.subject_en,
                preview_text_fr: d.preview_text_fr,
                preview_text_en: d.preview_text_en,
                body_html_fr: d.body_html_fr,
                body_html_en: d.body_html_en,
                delay_days: d.delay_days,
                active: d.active,
            };
            await api.put(`/admin/onboarding/templates/${id}`, payload);
            reload();
        } finally {
            setSaving("");
        }
    };

    const sendPreview = async (tplId) => {
        if (!previewEmail) {
            alert("Indiquez d'abord un email de test.");
            return;
        }
        setSaving(`preview-${tplId}`);
        try {
            await api.post(`/admin/onboarding/preview?tpl_id=${tplId}&to=${encodeURIComponent(previewEmail)}&lang=fr`);
            alert(`Aperçu FR envoyé à ${previewEmail}`);
        } catch (err) {
            alert(`Échec : ${err?.response?.data?.detail || "erreur"}`);
        } finally {
            setSaving("");
        }
    };

    const pending = queue.filter((q) => q.status === "pending").length;
    const sent = queue.filter((q) => q.status === "sent").length;

    return (
        <section className="bg-cp-ivoire border border-cp-border p-8 lg:col-span-2" data-testid="admin-onboarding">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <p className="cp-kicker text-cp-foret flex items-center gap-2">
                    <Mail size={14} strokeWidth={1.5} /> Newsletter onboarding — séquence J+0 / J+3 / J+7
                </p>
                <div className="flex items-center gap-4 cp-tiny text-cp-gris">
                    <span>{templates.length} templates</span>
                    <span className="text-cp-foret">{pending} en attente</span>
                    <span>{sent} envoyés (50 dernières)</span>
                </div>
            </div>

            <div className="bg-cp-craie border border-cp-border p-3 mb-6 flex items-center gap-3">
                <span className="cp-tiny text-cp-gris shrink-0">Email de test</span>
                <input
                    type="email"
                    placeholder="vous@exemple.com"
                    value={previewEmail}
                    onChange={(e) => setPreviewEmail(e.target.value)}
                    data-testid="onboarding-preview-email"
                    className="flex-1 bg-transparent border-b border-cp-border py-1 text-sm outline-none focus:border-cp-foret"
                />
            </div>

            <ul className="divide-y divide-cp-border">
                {templates.map((t) => {
                    const isOpen = open[t.id];
                    const d = draft[t.id] || t;
                    return (
                        <li key={t.id} className="py-4" data-testid={`onboarding-template-${t.id}`}>
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <p className="cp-serif text-lg text-cp-noir">
                                        <span className="cp-display-italic text-cp-sable-deep mr-3">D+{t.delay_days}</span>
                                        {t.subject_fr}
                                    </p>
                                    <p className="cp-tiny text-cp-gris mt-1">EN: {t.subject_en}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`cp-tiny ${t.active ? "text-cp-foret" : "text-red-700"}`}>
                                        {t.active ? "ACTIF" : "INACTIF"}
                                    </span>
                                    <button
                                        onClick={() => sendPreview(t.id)}
                                        disabled={saving === `preview-${t.id}`}
                                        data-testid={`onboarding-preview-${t.id}`}
                                        className="cp-tiny text-cp-foret hover:text-cp-noir flex items-center gap-1 border-b border-cp-foret pb-0.5"
                                    >
                                        <Send size={11} strokeWidth={1.5} /> Aperçu
                                    </button>
                                    <button
                                        onClick={() => setOpen({ ...open, [t.id]: !isOpen })}
                                        data-testid={`onboarding-toggle-${t.id}`}
                                        className="cp-tiny text-cp-noir hover:text-cp-foret flex items-center gap-1"
                                    >
                                        {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                        {isOpen ? "Replier" : "Modifier"}
                                    </button>
                                </div>
                            </div>

                            {isOpen && (
                                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-5 bg-cp-craie p-5 border border-cp-border" data-testid={`onboarding-edit-${t.id}`}>
                                    {/* FR */}
                                    <div className="space-y-3">
                                        <p className="cp-tiny text-cp-foret">Version française</p>
                                        <input
                                            value={d.subject_fr || ""}
                                            onChange={(e) => setDraft({ ...draft, [t.id]: { ...d, subject_fr: e.target.value } })}
                                            placeholder="Sujet FR"
                                            data-testid={`onboarding-${t.id}-subject-fr`}
                                            className="w-full bg-cp-ivoire border border-cp-border px-3 py-2 cp-serif text-base"
                                        />
                                        <input
                                            value={d.preview_text_fr || ""}
                                            onChange={(e) => setDraft({ ...draft, [t.id]: { ...d, preview_text_fr: e.target.value } })}
                                            placeholder="Texte d'aperçu (preview)"
                                            data-testid={`onboarding-${t.id}-preview-fr`}
                                            className="w-full bg-cp-ivoire border border-cp-border px-3 py-2 text-sm"
                                        />
                                        <textarea
                                            rows={10}
                                            value={d.body_html_fr || ""}
                                            onChange={(e) => setDraft({ ...draft, [t.id]: { ...d, body_html_fr: e.target.value } })}
                                            placeholder="Corps HTML FR — utilisez {site} pour insérer l'URL du site"
                                            data-testid={`onboarding-${t.id}-body-fr`}
                                            className="w-full bg-cp-ivoire border border-cp-border px-3 py-2 text-xs font-mono"
                                        />
                                    </div>
                                    {/* EN */}
                                    <div className="space-y-3">
                                        <p className="cp-tiny text-cp-foret">English version</p>
                                        <input
                                            value={d.subject_en || ""}
                                            onChange={(e) => setDraft({ ...draft, [t.id]: { ...d, subject_en: e.target.value } })}
                                            placeholder="Subject EN"
                                            data-testid={`onboarding-${t.id}-subject-en`}
                                            className="w-full bg-cp-ivoire border border-cp-border px-3 py-2 cp-serif text-base"
                                        />
                                        <input
                                            value={d.preview_text_en || ""}
                                            onChange={(e) => setDraft({ ...draft, [t.id]: { ...d, preview_text_en: e.target.value } })}
                                            placeholder="Preview text"
                                            data-testid={`onboarding-${t.id}-preview-en`}
                                            className="w-full bg-cp-ivoire border border-cp-border px-3 py-2 text-sm"
                                        />
                                        <textarea
                                            rows={10}
                                            value={d.body_html_en || ""}
                                            onChange={(e) => setDraft({ ...draft, [t.id]: { ...d, body_html_en: e.target.value } })}
                                            placeholder="Body HTML EN — use {site} for the site URL"
                                            data-testid={`onboarding-${t.id}-body-en`}
                                            className="w-full bg-cp-ivoire border border-cp-border px-3 py-2 text-xs font-mono"
                                        />
                                    </div>
                                    <div className="lg:col-span-2 flex justify-between items-center pt-3 border-t border-cp-border">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={!!d.active}
                                                onChange={(e) => setDraft({ ...draft, [t.id]: { ...d, active: e.target.checked } })}
                                                data-testid={`onboarding-${t.id}-active`}
                                                className="accent-cp-foret"
                                            />
                                            <span className="cp-tiny text-cp-noir">Template actif (envoyé automatiquement aux nouveaux inscrits)</span>
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <label className="flex items-center gap-2">
                                                <span className="cp-tiny text-cp-gris">Délai (jours)</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={d.delay_days ?? 0}
                                                    onChange={(e) => setDraft({ ...draft, [t.id]: { ...d, delay_days: parseInt(e.target.value || "0", 10) } })}
                                                    data-testid={`onboarding-${t.id}-delay`}
                                                    className="w-14 bg-cp-ivoire border border-cp-border px-2 py-1 text-sm"
                                                />
                                            </label>
                                            <button
                                                onClick={() => save(t.id)}
                                                disabled={saving === t.id}
                                                data-testid={`onboarding-save-${t.id}`}
                                                className="cp-kicker text-cp-foret border-b border-cp-foret pb-0.5 hover:text-cp-noir disabled:opacity-50"
                                            >
                                                {saving === t.id ? "Enregistrement…" : "Enregistrer"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
